import { useState, useRef, useEffect, MouseEventHandler } from "react";
import cloneDeep from "lodash/cloneDeep";
import clsx from "clsx";
// Helpers
import { isCellIssued, isFooterCell, normalizeCamelCase } from "@/Washouts/helpers/tables";
// Materials
import { getGridSingleSelectOperators, getGridStringOperators, GridValidRowModel, useGridApiRef } from "@mui/x-data-grid-premium";
// Custom components
import AddCellIssues from "@/Layouts/components/Tables/DataSmartTable/additional/AddCellIssues";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
// Types & Constants
import { CustomColDef, DynamicTableProps, Issue } from "@/Washouts/types/commons";
import { StringMap } from "@/Common/interfaces/types";
import { BASE_TABLE_PROPS } from "@/Washouts/constants";
import { Stack, Pagination, PaginationItem } from "@mui/material";
// Hooks
import useAggregationExtended from "@Layouts/components/Tables/DataSmartTable/aggregation/Aggregation";

const getFooterModelsFromColumns = (columns: CustomColDef[] = []) => {
	const aggregations: StringMap<string> = {};
	columns.forEach((column) => {
		if (column?.aggregation && column?.field) {
			aggregations[column.field] = column.aggregation;
		}
	});
	return aggregations;
};

function prepareNewColumns({ columns, issues, issuesReference }: Partial<DynamicTableProps>) {
	const newColumns: CustomColDef[] = [];
	columns?.forEach((column, key) => {
		const newColumn = { ...column } as CustomColDef;
		if (column.cellClassName?.set?.length || column.cellClassName?.conditions?.length) {
			const classNames = cloneDeep(column?.cellClassName?.set);
			const clsxObj: StringMap<boolean> = {};
			classNames?.forEach((className) => {
				clsxObj[className] = true;
			});
			const conditions = column.cellClassName?.conditions ?? [];
			newColumn["cellClassName"] = (params) => {
				const colorIssued = isCellIssued(params.row._id, params.field, issuesReference, issues);
				conditions?.forEach((condition) => {
					const className = condition?.className ?? "";
					clsxObj[className] = condition?.condition?.(params) ?? false;
				});
				return clsx({
					...clsxObj,
					"no-focus": !colorIssued,
					[`cell-color-${colorIssued?.replace("#", "")}`]: colorIssued,
				});
			};
		}
		if (typeof column.headerClassName === "object") {
			const headerClassName = column.headerClassName;
			if (headerClassName?.set?.length || headerClassName?.conditions?.length) {
				const classNames = cloneDeep(headerClassName?.set);
				const clsxObj: StringMap<boolean> = {};
				classNames?.forEach((className) => {
					clsxObj[className] = true;
				});
				const conditions = headerClassName?.conditions ?? [];
				newColumn["headerClassName"] = (params) => {
					conditions?.forEach((condition) => {
						const className = condition?.className ?? "";
						clsxObj[className] = condition?.condition?.(params) ?? false;
					});
					return clsx({
						...clsxObj,
					});
				};
			}
		}
		newColumns[key] = newColumn;
	});
	return newColumns;
}

// Adapt this function as required.
// Right now it can only handle the newCarsSales table.
const getPreWashoutTableComponents = (props: DynamicTableProps) => {
	const { pageSelector } = props;
	if (props?.tableRef !== "newCarsSales") {
		return <></>;
	}
	return pageSelector?.present ? (
		<Stack direction="row" alignItems={"center"} justifyContent={"center"} sx={{ paddingTop: "16px" }}>
			<Pagination
				count={pageSelector?.pagesLength}
				page={pageSelector?.activePage + 1}
				showFirstButton
				showLastButton
				onChange={(_event, page) => {
					pageSelector?.onActionUpdate?.(page - 1);
				}}
				classes={{ text: "pagination-theme" }}
				renderItem={({ page, ...item }) => (
					<PaginationItem {...item} page={pageSelector?.pages?.[+(page ?? 0) - 1 || 0]?.title ?? page} />
				)}
			/>
		</Stack>
	) : (
		<Stack direction="row" alignItems={"center"} justifyContent={"center"} sx={{ paddingRight: "50px", height: "48px" }}></Stack>
	);
};

const getGroupsAndSubgroupsInTable = (
	rows: {
		groupId: string;
		subgroupId: string;
		display?: GridValidRowModel[];
	}[]
) => {
	const groupId: string[] = [];
	const subgroupId: string[] = [];
	const allRows: {
		groupId: string;
		subgroupId: string;
		display?: GridValidRowModel[];
	}[] = [];
	rows?.forEach?.((row) => {
		if (row?.display?.length) {
			row.display.forEach((displayRow) => {
				// @ts-ignore
				allRows.push(displayRow);
			});
		} else {
			allRows.push(row);
		}
	});
	allRows.forEach((row) => {
		if (row?.groupId) {
			const splitByComma = row.groupId.split(",");
			if (splitByComma.length < 1) {
				groupId.push(row.groupId.toUpperCase());
			} else {
				splitByComma?.forEach?.((group) => {
					if (group) {
						groupId.push(group.toUpperCase());
					}
				});
			}
		}
		if (row?.subgroupId) {
			const splitByComma = row?.subgroupId.split(",");
			if (splitByComma.length < 1) {
				subgroupId.push(row?.subgroupId);
			} else {
				splitByComma
					?.filter?.((subgroup) => subgroup)
					.forEach((subgroup) => {
						subgroupId.push(subgroup);
					});
			}
		}
	});
	return { groupId: [...new Set(groupId)], subgroupId: [...new Set(subgroupId)] };
};

const getFilterOperatorsForGroupsAndSubgroups = () => {
	let filterOperators = [...getGridSingleSelectOperators()];
	const stringOperators = getGridStringOperators();
	stringOperators?.forEach?.((operator) => {
		filterOperators.push({ ...operator, InputComponent: filterOperators[0].InputComponent });
	});
	const isAnyOf = filterOperators.find((operator) => operator.value === "isAnyOf");
	const contains = filterOperators.find((operator) => operator.value === "contains");
	filterOperators = filterOperators.filter((operator) => ["is", "not"].includes(operator.value));
	if (isAnyOf && contains) {
		filterOperators.push(isAnyOf);
		filterOperators.push(contains);
		filterOperators.push({
			...isAnyOf,
			value: "contains any of",
			label: "contains any of",
			getApplyFilterFn: (filterItem) => (params) => {
				if (!filterItem?.value?.length) {
					return true;
				}
				return filterItem?.value?.some?.((value: string) => String(params).toUpperCase().includes(value.toUpperCase()));
			},
		});
		filterOperators.push({
			...isAnyOf,
			value: "does not contain any of",
			label: "does not contain any of",
			getApplyFilterFn: (filterItem) => (params) => {
				if (!filterItem?.value?.length) {
					return true;
				}
				return !filterItem?.value?.some?.((value: string) => String(params).toUpperCase().includes(value.toUpperCase()));
			},
		});
	}
	return filterOperators;
};

const addGroupAndSubgroupFilteringToColumns = (
	totalColumns: CustomColDef[],
	rows: { groupId: string; subgroupId: string; display?: GridValidRowModel[] }[]
) => {
	const filterOperators = getFilterOperatorsForGroupsAndSubgroups();
	const groupOptions = getGroupsAndSubgroupsInTable(rows);
	const columnsToUse = totalColumns;
	[
		{
			field: "groupId",
			groupTitle: "Group",
		},
		{
			field: "subgroupId",
			groupTitle: "Subgroup",
		},
	].forEach(({ field, groupTitle }) => {
		columnsToUse.push({
			field,
			filterOperators,
			headerName: groupTitle,
			sortable: false,
			flex: 1,
			align: "left",
			hideable: true,
			renderHeader: () => <></>,
			type: "singleSelect",
			valueOptions: (groupOptions[field as "groupId" | "subgroupId"] || []).map((item) =>
				field === "groupId" ? String(item).toUpperCase() : item
			),
			valueGetter: (_value: boolean, row: unknown) => {
				const sale = row as Record<string, string>;
				let value = String(sale?.[field]);
				if (field === "groupId") {
					value = String(value).toUpperCase();
				}
				return value;
			},
		});
	});
	return columnsToUse;
};

export default function DynamicTable(props: Readonly<DynamicTableProps>) {
	const {
		data = [],
		nouns = [],
		columns = [],
		subFooterClassName = "cell-contains-subtotal",
		title = {
			hide: false,
			label: "",
		},
		issuesReference = "",
		columnsToExclude = [],
		negatives = ["units", "retailUnits", "leaseUnits", "grossProfit", "retailGrossProfit", "leaseGrossProfit", "account"],
		pageSelector,
	} = props;

	const apiRef = useGridApiRef();

	const aggregations = useAggregationExtended({
		...props,
		departmentalTableSum: {},
		departmentalAccountPvr: {},
		departmentalAccountPvrWithContracts: {},
	});

	// Issues support attributes
	const [openAddIssues, setOpenAddIssues] = useState(false);
	const [anchorAddIssues, setAnchorAddIssues] = useState<(HTMLDivElement & EventTarget) | null>(null);
	const [issues, setIssues] = useState<Issue[]>([]);
	const activeIssue = useRef<Issue>(null);

	const getColumnById = (id: string) => {
		const sanitize = String(id);
		let attributes: CustomColDef = {
			// headerClassName,
			hideable: id === "id" || sanitize === "ID" || sanitize === "_id",
			field: sanitize,
			headerName: normalizeCamelCase(sanitize),
			sortable: false,
			align: "right",
			flex: 1,
			// cellClassName: getCellClassNameFn(issues),
		};

		if (id === "contracts" || id === "units") {
			attributes = {
				...attributes,
				type: "number",
				aggregation: "departmentalTableSum",
			};
		}
		return attributes;
	};

	const handleClickCell: MouseEventHandler<HTMLDivElement> = (event) => {
		activeIssue.current = {
			rowReference: event.currentTarget.parentElement?.dataset.id, // _id
			columnReference: event.currentTarget.dataset.field, // field
		};
		setAnchorAddIssues(event.currentTarget);
		setOpenAddIssues(true);
	};

	const updateCurrentIssue = (color: string, currentIssues: Issue[]) => {
		let isPresent = false;
		for (const issue of currentIssues) {
			if (
				issue &&
				issue.rowReference === activeIssue?.current?.rowReference &&
				issue.columnReference === activeIssue?.current?.columnReference
			) {
				issue.color = color;
				isPresent = true;
				break;
			}
		}

		if (!isPresent) {
			currentIssues.push({
				tableReference: props?.issuesReference,
				rowReference: activeIssue?.current?.rowReference ?? "",
				columnReference: activeIssue?.current?.columnReference ?? "",
				color,
			});
		}
		return currentIssues;
	};

	const handleChangeIssues = (color: string | null) => {
		let currentIssues = [...issues];
		if (color) {
			currentIssues = updateCurrentIssue(color, currentIssues);
		} else {
			currentIssues?.forEach((issue, key) => {
				if (
					issue &&
					issue.rowReference === activeIssue?.current?.rowReference &&
					issue.columnReference === activeIssue?.current?.columnReference
				) {
					currentIssues[key] = null;
				}
			});
			currentIssues = currentIssues.filter((issue) => issue);
		}

		setIssues(currentIssues);
		props?.onIssuesUpdatedAction?.(currentIssues, props?.issuesReference);
	};

	const getRowsAux = () => {
		const rowsAux: GridValidRowModel[] = [];
		const groups = nouns?.shift();
		if (groups && data[groups]?.length) {
			const traverse = data[groups];
			const subNoun = nouns.shift();
			traverse.forEach((group: Record<string, object[]>) => {
				if (subNoun && group?.[subNoun]?.length) {
					group[subNoun].forEach((item) => {
						rowsAux.push(item);
					});
				}
			});
		}
		return rowsAux;
	};

	const getKeys = (rowsAux: GridValidRowModel[] = []) => {
		const keys: string[] = [];
		let counter = 0;
		rowsAux?.forEach((row) => {
			if (!row?.id) {
				row.id = counter;
				counter += 1;
			}
			Object.entries(row).forEach(([key]) => {
				if (!keys.includes(key)) {
					keys.push(key);
				}
			});
		});
		return keys;
	};

	useEffect(() => {
		return () => {};
	}, [data, pageSelector]);

	let totalColumns: CustomColDef[] = [];
	let footerModels = {};
	let rows = [];

	if (data && columns) {
		const newColumns = prepareNewColumns({ columns, issues, issuesReference }) ?? [];
		footerModels = getFooterModelsFromColumns(newColumns);
		rows = data;
		totalColumns = newColumns;
	} else if (data && nouns?.length) {
		const columnsAux: CustomColDef[] = [];
		const rowsAux = getRowsAux();
		getKeys(rowsAux).forEach((key) => {
			if (!columnsToExclude?.some((column) => String(column).toUpperCase() === String(key).toUpperCase())) {
				const column = getColumnById(key);
				columnsAux.push(column);
			}
		});
		footerModels = getFooterModelsFromColumns(columnsAux);
		rows = rowsAux;
		totalColumns = columnsAux;
	}

	if (!totalColumns?.length) {
		return null;
	}

	const columnsToUse = addGroupAndSubgroupFilteringToColumns(totalColumns, rows);
	return (
		<>
			<>{getPreWashoutTableComponents(props)}</>
			<DataSmartTable
				{...props}
				enableCustomScrollbars={BASE_TABLE_PROPS.enableCustomScrollbars}
				apiRef={apiRef}
				columns={columnsToUse}
				rows={rows}
				disableColumnMenu
				disableRowSelectionOnClick
				disableMultipleRowSelection
				checkboxSelection={false}
				hideFooter={true}
				columnGroupingModel={props?.columnGroupingModel}
				columnVisibilityModel={{
					_id: false,
					groupId: false,
					subgroupId: false,
				}}
				initialState={{ aggregation: { model: footerModels } }}
				slots={{
					toolbar: CommonToolbar,
					noRowsOverlay: NoRowsOverlay,
				}}
				slotProps={{
					toolbar: {
						customTitle: {
							...title,
							title: title.label,
						},
						filter: {
							hide: props?.disableToolbar,
							disabled: props?.disableToolbar,
						},
						search: {
							data,
							hide: props?.disableToolbar,
							disabled: props?.disableToolbar,
						},
					},
					noRowsOverlay: {
						title: "No Data Available",
					},
					cell: props?.issues ? { onClick: handleClickCell } : undefined,
				}}
				getCellClassName={(params) => {
					const value = String(params?.formattedValue);
					const isNegative = negatives?.includes(params.field) && value?.length > 1 && ["(", "-"].includes(value.charAt(0));
					// Targeting footer
					if (isFooterCell(params) && isNegative) {
						// Use this to change the cell class name depending on the cell value
						return "aggregation-footer-negative";
					}

					// Append label 'Total' to the cell value in footer
					if (isFooterCell(params) && params.field === "type") {
						return "aggregation-footer-total";
					}

					if (params?.row?.subFooter || params?.row?.isFooter) {
						if (isNegative) {
							return "simple-negative-value aggregation-footer-negative";
						}
						return subFooterClassName;
					}
					return "";
				}}
				customProps={{
					...props.customProps,
					customAggregationsFn: aggregations,
				}}
				{...props.tableProps}
			/>
			{props.addIssues && (
				<AddCellIssues
					open={openAddIssues}
					anchorEl={anchorAddIssues}
					onCloseAction={() => {
						setOpenAddIssues(false);
						setAnchorAddIssues(null);
					}}
					onChangeAction={handleChangeIssues}
				/>
			)}
		</>
	);
}
